import { useNavigate, useOutletContext } from 'react-router-dom';

import { delegateTasksForStaff } from 'api/Wizard/Wizard';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

export const useDelegationActions = ({
  orchestratorData,
  steps,
  checkedSteps,
  checkedSubsteps,
  selectedStepIds,
  selectedSubstepIds,
  setSelectedStepIds,
  setSelectedSubstepIds,
  setIsDelegateModalOpen,
  setIsRemoveConfirmOpen,
  setIsRowClick,
  setRowAssignments,
  clearAllSelections,
  delegations
}) => {
  const navigate = useNavigate();
  const { practice } = useOutletContext();

  // Helper to check if all selected items have the same users
  const getCommonAssignments = () => {
    if (!delegations) return null;

    const allAssignmentSets = [];

    checkedSteps.forEach((step) => {
      const stepDelegation = delegations.steps?.find((s) => s.step_id === step.step_id);
      const userIds = (stepDelegation?.assignments || []).map((a) => a.user_id).sort();
      allAssignmentSets.push(userIds.join(','));
    });

    checkedSubsteps.forEach((substep) => {
      const substepDelegation = delegations.substeps?.find(
        (s) => s.substep_id === substep.substep_id
      );
      const userIds = (substepDelegation?.assignments || []).map((a) => a.user_id).sort();
      allAssignmentSets.push(userIds.join(','));
    });

    // If no selections, return null
    if (allAssignmentSets.length === 0) return null;

    // Check if all have the same users
    const firstSet = allAssignmentSets[0];
    const allSame = allAssignmentSets.every((set) => set === firstSet);

    if (!allSame || firstSet === '') return null;

    // Return the common assignments (from first found)
    const firstStepWithAssignments = checkedSteps.find((step) => {
      const stepDelegation = delegations.steps?.find((s) => s.step_id === step.step_id);
      return stepDelegation?.assignments?.length > 0;
    });

    if (firstStepWithAssignments) {
      const stepDelegation = delegations.steps?.find(
        (s) => s.step_id === firstStepWithAssignments.step_id
      );
      return stepDelegation?.assignments || [];
    }

    const firstSubstepWithAssignments = checkedSubsteps.find((substep) => {
      const substepDelegation = delegations.substeps?.find(
        (s) => s.substep_id === substep.substep_id
      );
      return substepDelegation?.assignments?.length > 0;
    });

    if (firstSubstepWithAssignments) {
      const substepDelegation = delegations.substeps?.find(
        (s) => s.substep_id === firstSubstepWithAssignments.substep_id
      );
      return substepDelegation?.assignments || [];
    }

    return null;
  };

  const handleOpenDelegateModal = () => {
    const stepIds = checkedSteps.map((s) => s.step_id);
    const substepIds = checkedSubsteps.map((s) => s.substep_id);
    const totalSelected = stepIds.length + substepIds.length;

    setSelectedStepIds(stepIds);
    setSelectedSubstepIds(substepIds);

    // Treat single selection like a row click
    if (totalSelected === 1) {
      setIsRowClick(true);
      const commonAssignments = getCommonAssignments();
      setRowAssignments(commonAssignments);
    } else {
      setIsRowClick(false);
      // Check if all selected items have the same assignments
      const commonAssignments = getCommonAssignments();
      // For multi-select, only pass users (no date/note), and only if all have same users
      if (commonAssignments && commonAssignments.length > 0) {
        // Pass assignments but clear note and due_date to indicate multi-select mode
        setRowAssignments(
          commonAssignments.map((a) => ({
            ...a,
            note: null,
            due_date: null
          }))
        );
      } else {
        setRowAssignments(null);
      }
    }

    setIsDelegateModalOpen(true);
  };

  const handleDelegateRow = (step, substep = null) => {
    setIsRowClick(true);

    if (substep) {
      setSelectedStepIds([]);
      setSelectedSubstepIds([substep.substep_id]);
      // Get assignments for this substep
      const substepDelegation = delegations?.substeps?.find(
        (s) => s.substep_id === substep.substep_id
      );
      setRowAssignments(substepDelegation?.assignments || null);
    } else {
      setSelectedStepIds([step.step_id]);
      setSelectedSubstepIds([]);
      // Get assignments for this step
      const stepDelegation = delegations?.steps?.find((s) => s.step_id === step.step_id);
      setRowAssignments(stepDelegation?.assignments || null);
    }

    setIsDelegateModalOpen(true);
  };

  const handleCloseDelegateModal = () => {
    setIsDelegateModalOpen(false);
    setSelectedStepIds([]);
    setSelectedSubstepIds([]);
    setIsRowClick(false);
    setRowAssignments(null);
  };

  const handleDelegateSuccess = () => {
    clearAllSelections();
    setRowAssignments(null);
    setIsRowClick(false);
  };

  // Get all user IDs that are assigned to the selected steps/substeps
  const getSelectedUserIds = () => {
    if (!delegations) return [];

    const userIds = new Set();

    checkedSteps.forEach((step) => {
      const stepDelegation = delegations.steps?.find((s) => s.step_id === step.step_id);
      stepDelegation?.assignments?.forEach((a) => {
        userIds.add(a.user_id);
      });
    });

    checkedSubsteps.forEach((substep) => {
      const substepDelegation = delegations.substeps?.find(
        (s) => s.substep_id === substep.substep_id
      );
      substepDelegation?.assignments?.forEach((a) => {
        userIds.add(a.user_id);
      });
    });

    return Array.from(userIds);
  };

  const getParentStepIdsForSubsteps = () => {
    const parentStepIds = new Set();

    checkedSubsteps.forEach((substep) => {
      const parentStep = steps.find((step) =>
        step.substeps?.some((s) => s.substep_id === substep.substep_id)
      );
      if (parentStep) {
        parentStepIds.add(parentStep.step_id);
      }
    });

    return Array.from(parentStepIds);
  };

  const { mutate: removeDelegation, isLoading: isRemoveLoading } = useCustomMutation({
    mutationFn: (params) => delegateTasksForStaff(navigate, params),
    onSuccess: () => {
      setIsRemoveConfirmOpen(false);
      clearAllSelections();
    },
    invalidateQueryKey: ['wizard-instance-delegation']
  });

  const handleOpenRemoveConfirm = () => {
    const stepIds = checkedSteps.map((s) => s.step_id);
    const substepIds = checkedSubsteps.map((s) => s.substep_id);
    setSelectedStepIds(stepIds);
    setSelectedSubstepIds(substepIds);
    setIsRemoveConfirmOpen(true);
  };

  const handleRemoveDelegation = () => {
    const userIds = getSelectedUserIds();
    if (userIds.length === 0) {
      setIsRemoveConfirmOpen(false);
      return;
    }

    const parentStepIds = getParentStepIdsForSubsteps();
    const allStepIds = Array.from(new Set([...selectedStepIds, ...parentStepIds]));

    removeDelegation({
      wizard_instance_id: orchestratorData.instanceId,
      practice_id: practice?.id,
      userIds,
      step_ids: allStepIds,
      substep_ids: selectedSubstepIds,
      operation: 'delete'
    });
  };

  return {
    handleOpenDelegateModal,
    handleDelegateRow,
    handleCloseDelegateModal,
    handleDelegateSuccess,
    handleOpenRemoveConfirm,
    handleRemoveDelegation,
    isRemoveLoading
  };
};
